import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, StatusPill } from "@/components/common";
import { PaginatedTable } from "@/components/PaginatedTable";
import { useApp } from "@/lib/store";
import { fmtDate, shortId } from "@/lib/utils";
import { Play, Network, X, Layers, RotateCw } from "lucide-react";

export interface DagStep {
  id: string;
  processor: string;
  config?: Record<string, unknown>;
}
export interface PipelineDag {
  steps?: DagStep[];
  edges?: [string, string][];
}
interface Pipeline {
  id: string; name: string; description: string;
  enabled?: boolean;
  dag?: PipelineDag;
}
interface PipelineRun {
  id: string; pipeline: string; status: string;
  triggered_by?: string;
  started_at?: string | null; finished_at?: string | null;
  summary?: Record<string, unknown>;
}
interface StepRun {
  id: string; run: string; step_id: string; processor_key: string;
  status: string;
  started_at?: string | null; finished_at?: string | null;
  output?: Record<string, unknown>;
  error?: string;
}
interface StreamCheckpoint {
  id: string; provider: string; stream: string; partition: string; status: string;
  source_position: number; acknowledged_position: number; lag: number; dlq_count: number;
}

export function Pipelines() {
  const ws = useApp((s) => s.workspaceId);
  const qc = useQueryClient();

  const [openPipeline, setOpenPipeline] = useState<Pipeline | null>(null);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  const probe = useQuery<{ count: number; results: Pipeline[] }>({
    queryKey: ["pipelines-probe", ws],
    queryFn: () => rawFetch(`/pipelines/pipelines/?page_size=1${ws ? `&workspace=${ws}` : ""}`),
  });

  const trigger = useMutation({
    mutationFn: (id: string) => rawFetch(`/pipelines/pipelines/${id}/trigger/`, {
      method: "POST", body: JSON.stringify({}),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["table", "/pipelines/runs/"] }),
  });

  const recover = useMutation({
    mutationFn: (id: string) => rawFetch(`/pipelines/runs/${id}/recover/`, {
      method: "POST", body: JSON.stringify({}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table", "/pipelines/runs/"] });
    },
  });
  const checkpoints = useQuery<{ results: StreamCheckpoint[] }>({
    queryKey: ["stream-checkpoints", ws],
    queryFn: () => rawFetch(`/pipelines/checkpoints/?page_size=100${ws ? `&workspace=${ws}` : ""}`),
    enabled: !!ws,
  });
  const checkpointAction = useMutation({
    mutationFn: ({ id, verb }: { id: string; verb: "acknowledge" | "replay" }) =>
      rawFetch(`/pipelines/checkpoints/${id}/${verb}/`, { method: "POST", body: JSON.stringify({}) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stream-checkpoints", ws] }),
  });

  const empty = !probe.isLoading && (probe.data?.count ?? 0) === 0;

  return (
    <>
      <PageHeader
        title="Pipelines"
        subtitle="Versioned DAGs that turn ingested content into graph entities."
        help={
          <>
            <p>Each pipeline is a directed graph of <strong>processors</strong> (parse, chunk, embed, extract, govern, graph-write). Steps run on the <code>ingestion</code> queue, write traces, and emit a summary; a failed step short-circuits the run but keeps its summary for debugging.</p>
            <p>Pipelines consume documents from <Link to="/catalog" className="text-accent hover:underline">Catalog</Link> and produce entities aligned with your <Link to="/ontology" className="text-accent hover:underline">ontology</Link>. Unlike agents, they are deterministic batch DAGs — pipelines build the graph, agents reason over it.</p>
          </>
        }
        learnMore={{ to: "/howto", hash: "pipelines", label: "How pipelines work" }}
      />

      {empty && (
        <div className="card mb-4 text-sm">
          No pipelines in this workspace yet. Install an industry{" "}
          <Link to="/marketplace" className="text-accent underline">Pack</Link>{" "}
          to get a ready-made <code>parse → chunk → embed → extract</code> DAG,
          then come back here to <strong>Trigger</strong> runs against the
          documents you ingested in{" "}
          <Link to="/catalog" className="text-accent underline">Catalog</Link>.
        </div>
      )}
      {probe.isError && <ErrorBox error={probe.error} />}

      <h2 className="mb-2 text-sm font-medium text-muted">Definitions</h2>
      <PaginatedTable<Pipeline>
        endpoint="/pipelines/pipelines/"
        baseParams={{ workspace: ws ?? undefined }}
        searchPlaceholder="Search by name or description…"
        cols={[
          { header: "Name", cell: (p) => (
              <button className="text-left text-fg hover:text-accent"
                      onClick={() => setOpenPipeline(p)}
                      title="View DAG">
                {p.name}
              </button>
          ) },
          { header: "Description", cell: (p) => <span className="text-muted">{p.description || "—"}</span> },
          { header: "Steps", cell: (p) => (
              <span className="text-muted">{p.dag?.steps?.length ?? 0}</span>
          ) },
          { header: "ID", cell: (p) => <code className="font-mono text-xs">{shortId(p.id)}</code> },
          { header: "", width: "1%", cell: (p) => (
              <div className="flex gap-1">
                <button className="btn-ghost text-[11px]"
                        onClick={() => setOpenPipeline(p)}>
                  <Network size={12} className="-mt-0.5 mr-1 inline" />DAG
                </button>
                <button className="btn-primary" disabled={trigger.isPending}
                        onClick={() => trigger.mutate(p.id)}>
                  <Play size={12} className="-mt-0.5 mr-1 inline" />Trigger
                </button>
              </div>
          )},
        ]}
        empty="No pipelines yet."
      />

      <h2 className="mb-2 mt-6 text-sm font-medium text-muted">Runs</h2>
      <PaginatedTable<PipelineRun>
        endpoint="/pipelines/runs/"
        baseParams={{ workspace: ws ?? undefined }}
        statusOptions={[
          { label: "Pending",   value: "pending"   },
          { label: "Running",   value: "running"   },
          { label: "Succeeded", value: "succeeded" },
          { label: "Failed",    value: "failed"    },
          { label: "Cancelled", value: "cancelled" },
        ]}
        searchPlaceholder="Search by pipeline name, triggered_by, or status…"
        refetchInterval={5000}
        cols={[
          { header: "ID", cell: (r) => (
              <button className="font-mono text-xs text-fg hover:text-accent"
                      onClick={() => setOpenRunId(r.id)}
                      title="View steps">
                {shortId(r.id)}
              </button>
          ) },
          { header: "Pipeline", cell: (r) => <code className="font-mono text-xs">{shortId(r.pipeline)}</code> },
          { header: "Status", cell: (r) => (
              <div className="flex flex-col">
                <StatusPill status={r.status} />
                {r.status === "failed" && r.summary && typeof r.summary.error === "string" && (
                  <span className="mt-0.5 max-w-[260px] truncate text-[10px] text-bad"
                        title={`${(r.summary.failed_step as string) || "?"}: ${r.summary.error}`}>
                    {(r.summary.failed_step as string) || "step"}: {r.summary.error as string}
                  </span>
                )}
              </div>
          ) },
          { header: "Started", cell: (r) => fmtDate(r.started_at) },
          { header: "Finished", cell: (r) => fmtDate(r.finished_at) },
          { header: "", width: "1%", cell: (r) => (
              <div className="flex items-center gap-1">
                {(r.status === "pending" || r.status === "failed") && (
                  <button className="btn-ghost text-[11px]"
                          onClick={() => recover.mutate(r.id)}
                          disabled={recover.isPending && recover.variables === r.id}
                          title={r.status === "pending"
                            ? "Re-dispatch this run (broker may have missed it)"
                            : "Reset and re-run from the start"}>
                    <RotateCw size={12} className="-mt-0.5 mr-1 inline" />
                    {r.status === "pending" ? "Recover" : "Retry"}
                  </button>
                )}
                <button className="btn-ghost text-[11px]"
                        onClick={() => setOpenRunId(r.id)}>
                  <Layers size={12} className="-mt-0.5 mr-1 inline" />Steps
                </button>
              </div>
          ) },
        ]}
      />

      <h2 className="mb-2 mt-6 text-sm font-medium text-muted">Stream checkpoints</h2>
      <div className="mb-3 rounded border border-warn/40 bg-warn/5 px-3 py-2 text-xs text-warn">
        Checkpoints provide resumable adapter state and DLQ evidence. They do not guarantee exactly-once writes across external systems.
      </div>
      {(checkpoints.data?.results?.length ?? 0) === 0 ? (
        <div className="card text-sm text-muted">No CDC adapter has reported a checkpoint for this workspace.</div>
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="min-w-full text-sm">
            <thead className="bg-border/30 text-left text-xs uppercase text-muted"><tr>
              <th className="px-3 py-2">Stream</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Offset</th><th className="px-3 py-2">Lag</th><th className="px-3 py-2">DLQ</th><th className="px-3 py-2">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-border">{checkpoints.data!.results.map((checkpoint) => (
              <tr key={checkpoint.id}>
                <td className="px-3 py-2"><code>{checkpoint.provider}:{checkpoint.stream}{checkpoint.partition ? `/${checkpoint.partition}` : ""}</code></td>
                <td className="px-3 py-2"><StatusPill status={checkpoint.status} /></td>
                <td className="px-3 py-2 font-mono text-xs">{checkpoint.acknowledged_position}/{checkpoint.source_position}</td>
                <td className="px-3 py-2 font-mono text-xs">{checkpoint.lag}</td>
                <td className="px-3 py-2 font-mono text-xs">{checkpoint.dlq_count}</td>
                <td className="px-3 py-2"><div className="flex gap-1">
                  <button className="btn-ghost text-xs" onClick={() => checkpointAction.mutate({ id: checkpoint.id, verb: "acknowledge" })}>Ack latest</button>
                  <button className="btn-ghost text-xs" onClick={() => checkpointAction.mutate({ id: checkpoint.id, verb: "replay" })}>Request replay</button>
                </div></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {checkpoints.isError && <ErrorBox error={checkpoints.error} />}

      {openPipeline && (
        <Drawer title={`DAG · ${openPipeline.name}`}
                onClose={() => setOpenPipeline(null)}>
          <DagView dag={openPipeline.dag} />
          <details className="mt-3 text-[11px] text-muted">
            <summary className="cursor-pointer">Raw JSON</summary>
            <pre className="mt-1 max-h-64 overflow-auto rounded border border-border bg-bg p-2 font-mono text-[10px]">
              {JSON.stringify(openPipeline.dag, null, 2)}
            </pre>
          </details>
        </Drawer>
      )}

      {openRunId && (
        <Drawer title={`Run · ${shortId(openRunId)}`}
                onClose={() => setOpenRunId(null)}>
          <RunStepsView runId={openRunId} />
        </Drawer>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

function Drawer({ title, onClose, children }: {
  title: string; onClose: () => void; children: React.ReactNode;
}) {
  return (
<div className="fixed inset-0 z-40 flex justify-end">
  <div
    className="flex-1 bg-black/20 dark:bg-black/40 backdrop-blur-[1px]"
    onClick={onClose}
  />

  <div
    className="
      h-full
      w-full
      max-w-2xl
      overflow-y-auto
      bg-card/95
      backdrop-blur-md
      border-l border-border
      p-4
      shadow-2xl
    "
    onClick={(e) => e.stopPropagation()}
  >
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-base font-medium">{title}</h3>

      <button
        type="button"
        className="btn-ghost"
        onClick={onClose}
        aria-label="Close"
      >
        <X size={14} />
      </button>
    </div>

    {children}
  </div>
</div>
  );
}

export function DagView({ dag }: { dag: PipelineDag | undefined }) {
  const steps = dag?.steps ?? [];
  const edges: [string, string][] = (dag?.edges ?? []).filter(
    (e): e is [string, string] => Array.isArray(e) && e.length === 2
  );

  if (steps.length === 0) {
    return (
      <div className="card text-center text-xs text-muted">
        This pipeline has no steps. Author the DAG via
        <code className="mx-1">POST /pipelines/pipelines/</code>
        or install a Pack.
      </div>
    );
  }

  const rank = new Map<string, number>();
  const incoming = new Map<string, Set<string>>();
  steps.forEach((s) => incoming.set(s.id, new Set()));
  edges.forEach(([f, t]) => {
    if (incoming.has(t)) incoming.get(t)!.add(f);
  });

  let frontier = steps.filter((s) => (incoming.get(s.id)?.size ?? 0) === 0);
  if (frontier.length === 0) frontier = [steps[0]];
  let level = 0;
  const placed = new Set<string>();
  while (frontier.length && placed.size < steps.length) {
    frontier.forEach((s) => { rank.set(s.id, level); placed.add(s.id); });
    const next = steps.filter((s) =>
      !placed.has(s.id) &&
      [...(incoming.get(s.id) ?? [])].every((p) => placed.has(p))
    );
    if (next.length === 0) {
      const leftovers = steps.filter((s) => !placed.has(s.id));
      leftovers.forEach((s) => { rank.set(s.id, level + 1); placed.add(s.id); });
      break;
    }
    frontier = next;
    level += 1;
  }

  const ranks: DagStep[][] = [];
  steps.forEach((s) => {
    const r = rank.get(s.id) ?? 0;
    if (!ranks[r]) ranks[r] = [];
    ranks[r].push(s);
  });

  const colW = 210;
  const rowH = 84;
  const padX = 28;
  const padY = 28;
  const NW = 158;   // node width
  const NH = 56;    // node height
  const W = padX * 2 + Math.max(1, ranks.length) * colW;
  const H = padY * 2 + Math.max(1, ...ranks.map((r) => r.length)) * rowH;

  const pos = new Map<string, { x: number; y: number }>();
  ranks.forEach((col, ci) => {
    col.forEach((s, ri) => {
      pos.set(s.id, {
        x: padX + ci * colW + colW / 2,
        y: padY + ri * rowH + rowH / 2,
      });
    });
  });
  const order = new Map<string, number>();
  steps.forEach((s, i) => order.set(s.id, i));

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-4 rounded-sm bg-gradient-to-r from-accent/80 to-accent/30" />
          {steps.length} step{steps.length === 1 ? "" : "s"}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-px w-5 bg-accent/60" />
          {edges.length} edge{edges.length === 1 ? "" : "s"}
        </span>
        <span className="ml-auto rounded-full border border-border bg-bg/60 px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted/80">
          read-only render
        </span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border bg-gradient-to-b from-panel to-bg p-3 shadow-inner">
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: W }}>
          <defs>
            <pattern id="dag-grid" width="22" height="22" patternUnits="userSpaceOnUse">
              <path d="M22 0H0V22" fill="none" stroke="hsl(var(--accent) / 0.06)" strokeWidth="1" />
            </pattern>
            <linearGradient id="dag-node" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--panel))" />
              <stop offset="100%" stopColor="hsl(var(--bg))" />
            </linearGradient>
            <linearGradient id="dag-edge" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="hsl(var(--accent) / 0.25)" />
              <stop offset="100%" stopColor="hsl(var(--accent) / 0.85)" />
            </linearGradient>
            <marker id="dag-arrow" markerWidth="10" markerHeight="10"
                    refX="8" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" fill="hsl(var(--accent))" />
            </marker>
            <filter id="dag-shadow" x="-20%" y="-20%" width="140%" height="160%">
              <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#000" floodOpacity="0.35" />
            </filter>
          </defs>

          <rect x="0" y="0" width={W} height={H} fill="url(#dag-grid)" rx="10" />

          {edges.map(([from, to], i) => {
            const a = pos.get(from); const b = pos.get(to);
            if (!a || !b) return null;
            const x1 = a.x + NW / 2, x2 = b.x - NW / 2;
            const mx = (x1 + x2) / 2;
            const d = `M ${x1} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${x2} ${b.y}`;
            return (
              <path key={i} d={d} fill="none" stroke="url(#dag-edge)"
                    strokeWidth="1.75" markerEnd="url(#dag-arrow)" />
            );
          })}

          {steps.map((s) => {
            const p = pos.get(s.id)!;
            const idx = (order.get(s.id) ?? 0) + 1;
            return (
              <g key={s.id} transform={`translate(${p.x - NW / 2}, ${p.y - NH / 2})`}>
                <rect width={NW} height={NH} rx="10" fill="url(#dag-node)"
                      stroke="hsl(var(--accent) / 0.35)" strokeWidth="1"
                      filter="url(#dag-shadow)" />
                {/* accent leading edge — gives the node a directional, app-grade feel */}
                <rect width="4" height={NH} rx="2" fill="hsl(var(--accent))" />
                <rect x="-1" width="6" height={NH} fill="url(#dag-node)" opacity="0" />
                {/* step index chip */}
                <circle cx="22" cy={NH / 2} r="11" fill="hsl(var(--accent) / 0.12)"
                        stroke="hsl(var(--accent) / 0.5)" strokeWidth="1" />
                <text x="22" y={NH / 2 + 3.5} textAnchor="middle"
                      fontSize="10" fontWeight="700" fill="hsl(var(--accent))">
                  {idx}
                </text>
                <text x="40" y={NH / 2 - 4} fontSize="11.5" fontWeight="600"
                      className="fill-fg">
                  {s.id.length > 16 ? s.id.slice(0, 15) + "…" : s.id}
                </text>
                <text x="40" y={NH / 2 + 11} fontSize="9.5" className="fill-muted">
                  {s.processor.length > 18 ? s.processor.slice(0, 17) + "…" : s.processor}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function RunStepsView({ runId }: { runId: string }) {
  const qc = useQueryClient();
  const q = useQuery<{ count: number; results: StepRun[] }>({
    queryKey: ["pipeline-steps", runId],
    queryFn: () => rawFetch(
      `/pipelines/steps/?run=${encodeURIComponent(runId)}&page_size=200&ordering=created_at`),
    refetchInterval: (qq) => {
      const rows = qq.state.data?.results ?? [];
      const live = rows.some((r) => r.status === "running" || r.status === "pending");
      return live ? 3000 : false;
    },
  });

  const recover = useMutation({
    mutationFn: () => rawFetch(`/pipelines/runs/${runId}/recover/`, {
      method: "POST", body: JSON.stringify({}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pipeline-steps", runId] });
      qc.invalidateQueries({ queryKey: ["table", "/pipelines/runs/"] });
    },
  });

  if (q.isLoading) return <div className="text-xs text-muted">Loading steps…</div>;
  if (q.isError) return <ErrorBox error={q.error} />;
  const rows = q.data?.results ?? [];
  if (rows.length === 0) {
    return (
      <div className="card space-y-3 text-center text-xs text-muted">
        <div>
          No StepRuns recorded yet — the run is pending and the worker
          has not picked it up.
        </div>
        <div className="text-[11px]">
          This usually means the Celery worker for the
          {" "}<code>ingestion</code>{" "}queue is offline, or the original
          {" "}<code>task.delay</code>{" "}was lost. Use <strong>Recover</strong>
          to re-dispatch; if the broker is down it will fall back to
          inline execution so the run still completes.
        </div>
        <div>
          <button className="btn-primary inline-flex items-center gap-1 text-xs"
                  onClick={() => recover.mutate()}
                  disabled={recover.isPending}>
            <RotateCw size={12} />
            {recover.isPending ? "Re-dispatching…" : "Recover run"}
          </button>
        </div>
        {recover.isError && <ErrorBox error={recover.error} />}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {rows.map((s) => (
        <div key={s.id} className="rounded border border-border p-2 text-xs">
          <div className="flex items-center gap-2">
            <StatusPill status={s.status} />
            <span className="font-medium">{s.step_id}</span>
            <code className="text-[10px] text-muted">{s.processor_key}</code>
            <span className="ml-auto text-[10px] text-muted">
              {fmtDate(s.started_at)} → {fmtDate(s.finished_at)}
            </span>
          </div>
          {s.error && (
            <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-bad/5 p-1 text-[10px] text-bad">
              {s.error}
            </pre>
          )}
          {s.output && Object.keys(s.output).length > 0 && (
            <details className="mt-1 text-[11px] text-muted">
              <summary className="cursor-pointer">Output</summary>
              <pre className="mt-1 max-h-40 overflow-auto rounded bg-bg p-1 font-mono text-[10px]">
                {JSON.stringify(s.output, null, 2)}
              </pre>
            </details>
          )}
        </div>
      ))}
    </div>
  );
}
